/* eslint-disable no-unneeded-ternary */
import { Link } from "react-router-dom";
import tw, { theme } from "twin.macro";
import { memo, useEffect, useId, useRef } from "react";
import Swiper from "swiper";
import { A11y, Navigation, Pagination } from "swiper/modules";

import Button from "@components/Atoms/Button";
import Heading from "@/components/Atoms/Heading";
import ProductGallerySkeleton from "@components/Atoms/ProductCardGallerySkeleton";
import ProductCardGallery from "@/components/Molecules/ProductCardGallery";

import IconChevronLeft from "@iconsV2/Arrow/chevron-left-normal.svg?react";
import IconChevronRight from "@iconsV2/Arrow/chevron-right-normal.svg?react";

// import ProductGallery from "../ProductGallery";
import { type TOrientation } from "@/types/common";
import { useMediaQuery } from "@/hooks/useMediaQuery";

import {
  StyledWrapperHead,
  StyledWrapperRoot,
  StyledWrapperSwiperProductGallery,
  StyledButtonNext,
  StyledButtonPrev,
  StylesWrapperPagination,
} from "./styles";

export interface ISwiperSectionProductGallerySmall {
  items: Cart[];
  isLoading?: boolean;
  title?: string;
  showViewMore?: true;
  to: string;
  recommId?: string;
  [key: string]: any;
}

export interface ISwiperSectionProductGallerySmallShow {
  items: Cart[];
  isLoading?: boolean;
  title?: string;
  showViewMore?: false;
  recommId?: string;
  orientation?: TOrientation;
  [key: string]: any;
}

type ISwiperProductGalleryProps = ISwiperSectionProductGallerySmall | ISwiperSectionProductGallerySmallShow;

const hasToProp = (props: ISwiperProductGalleryProps): props is ISwiperSectionProductGallerySmall => {
  return (props as ISwiperSectionProductGallerySmallShow).to !== undefined;
};

const SwiperSectionProductGallerySmall = (props: ISwiperProductGalleryProps) => {
  const {
    items = [],
    isLoading = false,
    title = "",
    showViewMore = false,
    recommId = "",
    orientation = "horizontal",
  } = props;

  const max1060 = useMediaQuery("(max-width: 1060px)");
  const id = useId();
  const swiperRef = useRef<Swiper | null>(null);
  const swiperClassName = `swiper-container-products-small-${id.replace(/:/g, "")}`;
  const swiperPaginationClassName = `${swiperClassName}-pagination`;
  const swiperNavigationClassName = `${swiperClassName}-navigation`;

  const linkProps = showViewMore && hasToProp(props) ? { as: Link, to: props.to } : {};

  useEffect(() => {
    if (!isLoading) {
      swiperRef.current = new Swiper(`.${swiperClassName}`, {
        modules: [Pagination, Navigation, A11y],
        spaceBetween: 10,
        grabCursor: true,
        pagination: {
          el: `.${swiperPaginationClassName}`,
          clickable: true,
          dynamicBullets: true,
        },
        navigation: {
          nextEl: `.${swiperNavigationClassName}-next`,
          prevEl: `.${swiperNavigationClassName}-prev`,
          hiddenClass: "swiper-navigation-hidden",
          disabledClass: "swiper-navigation-disabled",
        },
        slidesPerView: "auto",
        breakpoints: {
          1060: {
            slidesPerView: items.length < 4 ? 3 : 4,
          },
          [theme`screens.large`.replace("px", "")]: {
            slidesPerView: items.length < 5 ? 3 : 5,
          },
        },
      });
      return () => {
        if (swiperRef.current) {
          swiperRef.current.destroy(true, true);
        }
      };
    }
  }, [isLoading, items?.length]);

  if (isLoading) {
    return (
      <StyledWrapperRoot>
        <StyledWrapperHead tw="my-4">
          <div css={tw`animate-pulse1 w-40 h-5 medium:h-6 bg-primaryV2-60 rounded-lg`} />

          {/* <div css={tw`animate-pulse1 w-32 h-5 medium:h-6 bg-primaryV2-60 rounded-lg`} /> */}
        </StyledWrapperHead>

        <StyledWrapperSwiperProductGallery tw="overflow-hidden flex gap-2">
          {Array.from({ length: 7 }).map((_, index) => (
            <article tw="flex-1" key={index.toString()}>
              <ProductGallerySkeleton sizeVariant="sm" orientation={orientation} />
            </article>
          ))}
        </StyledWrapperSwiperProductGallery>
      </StyledWrapperRoot>
    );
  }

  return (
    <StyledWrapperRoot>
      <StyledWrapperHead>
        {isLoading ? (
          <div css={tw`animate-pulse1 w-40 h-6 bg-primaryV2-60 rounded-lg`} />
        ) : (
          <Heading type="h5" {...linkProps}>
            {title}
          </Heading>
        )}

        {isLoading ? (
          <div css={tw`animate-pulse1 w-32 h-6 bg-primaryV2-60 rounded-lg`} />
        ) : (
          <>
            {showViewMore && (
              <Button
                sizeVariant="md"
                variant="text"
                as={Link}
                to={props.to}
                color="secondary"
                endIcon={<IconChevronRight />}
              >
                Ver mas
              </Button>
            )}
          </>
        )}
      </StyledWrapperHead>
      <StyledWrapperSwiperProductGallery
        className={`swiper ${swiperClassName}`}
        $isSmall={max1060}
        $isLoading={isLoading}
      >
        <div className="swiper-wrapper">
          {items.map((product, index) => {
            return (
              <article className="swiper-slide" key={`${product.product.id}-${index}`}>
                <ProductCardGallery
                  item={product}
                  sizeVariant="sm"
                  orientation={orientation}
                  recommId={recommId}
                />
              </article>
            );
          })}
        </div>
      </StyledWrapperSwiperProductGallery>

      <StylesWrapperPagination className={`${swiperPaginationClassName} swiper-pagination`} />

      <StyledButtonPrev
        sizeVariant="sm"
        className={`${swiperNavigationClassName}-prev`}
        onClick={() => swiperRef.current?.slidePrev()}
        variant="outlined"
        color="primary"
        startIcon={<IconChevronLeft />}
      />

      <StyledButtonNext
        sizeVariant="sm"
        className={`${swiperNavigationClassName}-next`}
        onClick={() => swiperRef.current?.slideNext()}
        variant="outlined"
        color="primary"
        startIcon={<IconChevronRight />}
      />
    </StyledWrapperRoot>
  );
};

export default memo(SwiperSectionProductGallerySmall);
